import { CommonUtility } from '@utility/common';
import {
  CaretDown,
  CaretRight,
  GlobeHemisphereEast,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { CATALOG_SETTING_COLUMNS } from '@utility/constants/catalog';
import { Flag } from '@elements/Flag';
import { AdsBar, MarketBar } from './MarketBar';
import {
  AppTableBody,
  AppTableHeader,
  AppTableRow,
  AppTableHeaderCell,
  AppTableRoot,
} from '@components/AppTable';

type RegionMarketplace = {
  _id?: string;
  country_code?: string;
  is_seed?: boolean;
  sync_settings?: Record<string, unknown>;
  continent?: { name?: string };
};

type CountryGroup = {
  country_code?: string;
  name?: string;
  marketplaces: RegionMarketplace[];
};

type Country = { code?: string; name?: string };

function Location({
  market,
  isUser,
  updateCredentials,
  doProgressSync,
  disconnectMarket,
  reconnect,
  refreshMetadata,
}) {
  return (
    /* Seven columns cannot fit a phone, so the table scrolls inside its card. */
    <div className="sk:w-full sk:overflow-x-auto">
      <AppTableRoot className="sk:min-w-[900px]">
        <AppTableHeader>
          <AppTableRow>
            <AppTableHeaderCell colSpan={CATALOG_SETTING_COLUMNS.length}>
              <div className="sk:flex sk:items-center sk:gap-2 sk:text-sm sk:font-semibold sk:text-fg">
                <Flag width={30} height={20} name={market.country_code} />
                <span>{market.name}</span>
              </div>
            </AppTableHeaderCell>
          </AppTableRow>
          <AppTableRow>
            {CATALOG_SETTING_COLUMNS.map(column => (
              <AppTableHeaderCell
                key={column.key}
                textAlign={column.textAlign || 'center'}
                width={column.width}
              >
                {column.text}
              </AppTableHeaderCell>
            ))}
          </AppTableRow>
        </AppTableHeader>
        <AppTableBody>
          {market.marketplaces.map(marketplace => (
            <MarketBar
              key={marketplace._id}
              isUser={isUser}
              updateCredentials={updateCredentials}
              doProgressSync={doProgressSync}
              disconnectMarket={disconnectMarket}
              reconnect={reconnect}
              item={marketplace}
              refreshMetadata={refreshMetadata}
            />
          ))}
        </AppTableBody>
      </AppTableRoot>
    </div>
  );
}

export function LocationMarket({
  connectedMarketPlaces,
  connectedAds = [],
  title,
  isUser,
  updateCredentials,
  doProgressSync = true,
  disconnectMarket,
  reconnect,
  countries,
  refreshMetadata,
}) {
  const [isOpen, setIsOpen] = useState(true);

  const toggleOpen = () => setIsOpen(!isOpen);

  const selected: RegionMarketplace[] = connectedMarketPlaces.filter(
    d => d.continent.name === title,
  );

  // @ts-expect-error - TS2339: Property 'continent' does not exist on type 'never'.
  const selectedAds = connectedAds.filter(d => d?.continent === title);

  const countryList: Country[] = countries;

  const marketplacesRegionSplit = useMemo(() => {
    if (!CommonUtility.isValidArray(selected)) return [];
    if (!CommonUtility.isValidArray(countryList)) return [];
    const arr: CountryGroup[] = [];
    selected.forEach(market => {
      const existingCountryIndex = arr.findIndex(
        x => x.country_code === market.country_code,
      );
      const newMarketplace: RegionMarketplace = {
        ...market,
        country_code: market.country_code,
        _id: market._id,
        sync_settings: {
          ...(market.sync_settings || {}),
          is_seed: market.is_seed,
        },
      };
      if (existingCountryIndex > -1) {
        arr[existingCountryIndex].marketplaces = [
          ...arr[existingCountryIndex].marketplaces,
          newMarketplace,
        ];
      } else {
        const countryName = countryList.find(
          x => x.code === market.country_code,
        )?.name;
        arr.push({
          country_code: market.country_code,
          name: countryName,
          marketplaces: [newMarketplace],
        });
      }
    });
    return arr;
  }, [selected, countryList]);

  return (
    <section className="sk:flex sk:flex-col sk:gap-3">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={toggleOpen}
        className="sk:m-0 sk:flex sk:w-full sk:cursor-pointer sk:appearance-none sk:items-center sk:justify-between sk:gap-3 sk:border-0 sk:bg-transparent sk:p-0 sk:py-2 sk:text-left sk:font-sans sk:outline-none sk:focus-visible:outline-2 sk:focus-visible:outline-primary"
      >
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2 sk:text-base sk:font-semibold sk:text-fg">
          <GlobeHemisphereEast size={22} aria-hidden className="sk:shrink-0" />
          <span className="sk:truncate">{title}</span>
        </span>
        <span className="sk:flex sk:shrink-0 sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
          <span>
            {selected.length} Marketplace{selected.length > 1 ? 's' : ''},
            &nbsp;{marketplacesRegionSplit.length}{' '}
            {marketplacesRegionSplit.length > 1 ? 'Countries' : 'Country'}
          </span>
          {isOpen ? (
            <CaretDown size={20} aria-hidden className="sk:text-fg-tertiary" />
          ) : (
            <CaretRight size={20} aria-hidden className="sk:text-fg-tertiary" />
          )}
        </span>
      </button>
      {isOpen && (
        <div className="sk:flex sk:flex-col sk:gap-4">
          {marketplacesRegionSplit.map(market => (
            <Location
              key={market.country_code}
              market={market}
              isUser={isUser}
              updateCredentials={updateCredentials}
              doProgressSync={doProgressSync}
              disconnectMarket={disconnectMarket}
              reconnect={reconnect}
              refreshMetadata={refreshMetadata}
            />
          ))}
          {CommonUtility.isValidArray(selectedAds) &&
            // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
            selectedAds.map(d => <AdsBar data={d} key={d._id} />)}
        </div>
      )}
    </section>
  );
}
